import { EntityShareRequest } from '../../icc-api/model/requests/EntityShareRequest'
import { IccDataOwnerXApi } from '../icc-data-owner-x-api'
import { UserEncryptionKeysManager } from './UserEncryptionKeysManager'
import { CryptoStrategies } from './CryptoStrategies'
import { EncryptedEntityWithType, EntityWithDelegationTypeName } from '../utils/EntityWithDelegationTypeName'
import { LruTemporisedAsyncCache } from '../utils/lru-temporised-async-cache'
import { fingerprintV1toV2, fingerprintV2, hexPublicKeysWithSha1Of, hexPublicKeysWithSha256Of } from './utils'
import { ExchangeDataManager } from './ExchangeDataManager'
import { ExchangeData } from '../../icc-api/model/internal/ExchangeData'
import { SecureDelegationsEncryption } from './SecureDelegationsEncryption'
import { CryptoPrimitives } from './CryptoPrimitives'
import { hex2ua, ua2ab, ua2hex } from '../utils'
import { AccessControlSecretUtils } from './AccessControlSecretUtils'
import { EntityShareOrMetadataUpdateRequest } from '../../icc-api/model/requests/EntityShareOrMetadataUpdateRequest'
import { EncryptedEntity, EncryptedEntityStub } from '../../icc-api/model/models'
import { SecureDelegation } from '../../icc-api/model/SecureDelegation'
import { EntitySharedMetadataUpdateRequest } from '../../icc-api/model/requests/EntitySharedMetadataUpdateRequest'
import EntryUpdateTypeEnum = EntitySharedMetadataUpdateRequest.EntryUpdateTypeEnum
import AccessLevelEnum = SecureDelegation.AccessLevelEnum
import { SecurityMetadata } from '../../icc-api/model/SecurityMetadata'
import { ExchangeDataMapManager } from './ExchangeDataMapManager'
import { ShaVersion } from './RSA'

export class SecureDelegationsManager {
  constructor(
    private readonly exchangeDataManager: ExchangeDataManager,
    private readonly exchangeDataMapManager: ExchangeDataMapManager,
    private readonly secureDelegationsEncryption: SecureDelegationsEncryption,
    private readonly accessControlSecretUtils: AccessControlSecretUtils,
    private readonly userKeys: UserEncryptionKeysManager,
    private readonly primitives: CryptoPrimitives,
    private readonly dataOwnerApi: IccDataOwnerXApi,
    private readonly cryptoStrategies: CryptoStrategies,
    private readonly selfNeedsAnonymousDelegations: boolean
  ) {}

  private readonly dataOwnerInfoCache: LruTemporisedAsyncCache<
    string,
    {
      requiresAnonymousDelegations: boolean
      availablePublicKeysHexWithSha1: string[]
      availablePublicKeysHexWithSha256: string[]
    }
  > = new LruTemporisedAsyncCache(100, () => 30 * 60 * 1000)

  /**
   * Note: this method does not save the updated entity.
   * @param entity an entity, must already have secret foreign keys initialised.
   * @param entityType the type of the entity
   * @param secretIds the initial secret ids to include and share with the auto-delegations
   * @param owningEntityIds the initial owning entity ids to include and share with the auto-delegations
   * @param encryptionKeys the initial encryption keys to include and share with the auto-delegations
   * @param autoDelegations the data owners which will initially have access to the entity in addition to the current data owner and the access level
   * they will have on the entity.
   * @param alternateRootDelegation by default a new entity is created with a root delegation from self to self. In keyless mode this is not possible,
   * and instead the root delegation will be from self to another. You have to specify which delegate will be part of the root delegation.
   * @return the entity with the security metadata initialised for the provided parameters.
   */
  async entityWithInitialisedEncryptedMetadata<T extends EncryptedEntity>(
    entity: T & { secretForeignKeys: string[] },
    entityType: EntityWithDelegationTypeName,
    secretIds: string[],
    owningEntityIds: string[],
    encryptionKeys: string[],
    autoDelegations: { [delegateId: string]: SecureDelegation.AccessLevelEnum },
    alternateRootDelegation: string | undefined
  ): Promise<T> {
    const entityWithType: EncryptedEntityWithType = { entity, type: entityType }
    const selfId = await this.dataOwnerApi.getCurrentDataOwnerId()
    const rootDelegationDelegate = alternateRootDelegation ?? selfId
    const rootDelegationInfo = await this.makeSecureDelegationInfo(
      entityWithType,
      rootDelegationDelegate,
      secretIds,
      encryptionKeys,
      owningEntityIds,
      AccessLevelEnum.WRITE,
      undefined
    )
    const otherDelegationsInfo: {
      delegationKey: string
      accessControlKeyHex: string
      delegation: SecureDelegation
      encryptedExchangeDataId: { [fp: string]: string } | undefined
    }[] = []
    for (const [delegateId, permissions] of Object.entries(autoDelegations)) {
      if (delegateId !== rootDelegationDelegate) {
        otherDelegationsInfo.push(
          await this.makeSecureDelegationInfo(
            entityWithType,
            delegateId,
            secretIds,
            encryptionKeys,
            owningEntityIds,
            permissions,
            rootDelegationInfo.delegationKey
          )
        )
      }
    }
    const secureDelegations = Object.fromEntries(
      [rootDelegationInfo, ...otherDelegationsInfo].map(({ delegationKey, delegation }) => [delegationKey, delegation])
    )
    const delegationForExchangeDataMaps = selfId == rootDelegationDelegate ? otherDelegationsInfo : [rootDelegationInfo, ...otherDelegationsInfo]
    const newExchangeDataMaps = Object.fromEntries(
      delegationForExchangeDataMaps
        .filter(({ encryptedExchangeDataId }) => !!encryptedExchangeDataId)
        .map(({ accessControlKeyHex, encryptedExchangeDataId }) => [accessControlKeyHex, encryptedExchangeDataId!])
    )
    await this.exchangeDataMapManager.createExchangeDataMaps(newExchangeDataMaps)
    return {
      ...entity,
      securityMetadata: new SecurityMetadata({ secureDelegations }),
    }
  }

  /**
   * Make a request for sharing an entity with a delegate or to update existing shared metadata by adding additional secretIds, encryptionKeys or
   * owningEntityIds if there is already some metadata shared between the current data owner and the delegate. In case there is already a delegation
   * for the delegate, and it already contains all the provided metadata, this method returns undefined, since there is no need to make any request to
   * share the provided data.
   * @param entityWithType an entity with type
   * @param delegateId the id of the delegate
   * @param shareSecretIds the secret ids to share
   * @param shareEncryptionKeys the encryption keys to share
   * @param shareOwningEntityIds the owning entity ids to share
   * @param newDelegationPermissions the permissions to grant to the delegate in case a new delegation needs to be created. If this method creates an
   * update request instead of a share request, this parameter is ignored.
   * @return the request to share the entity, or the request to update the shared metadata for the entity, or undefined if there is no need to update
   * the entity to allow the delegate to access the provided data.
   */
  async makeShareOrUpdateRequestParams(
    entityWithType: EncryptedEntityWithType,
    delegateId: string,
    shareSecretIds: string[],
    shareEncryptionKeys: string[],
    shareOwningEntityIds: string[],
    newDelegationPermissions: EntityShareRequest.RequestedPermissionInternal
  ): Promise<EntityShareOrMetadataUpdateRequest | undefined> {
    const exchangeDataInfo = await this.exchangeDataManager.getOrCreateEncryptionDataTo(delegateId)
    const secureDelegationKey = await this.accessControlSecretUtils.secureDelegationKeyFor(exchangeDataInfo.accessControlSecret, entityWithType.type)
    const existingSecureDelegation = entityWithType.entity.securityMetadata?.secureDelegations?.[secureDelegationKey]
    if (existingSecureDelegation) {
      const updateParams = await this.makeUpdateRequestParams(
        secureDelegationKey,
        existingSecureDelegation,
        exchangeDataInfo,
        shareSecretIds,
        shareEncryptionKeys,
        shareOwningEntityIds
      )
      return updateParams ? { update: updateParams } : undefined
    } else {
      const accessControlKeys = ua2hex(
        await this.accessControlSecretUtils.accessControlKeyFor(exchangeDataInfo.accessControlSecret, entityWithType.type)
      )
      return {
        share: await this.makeShareRequestParams(
          exchangeDataInfo,
          accessControlKeys,
          delegateId,
          shareSecretIds,
          shareEncryptionKeys,
          shareOwningEntityIds,
          newDelegationPermissions
        ),
      }
    }
  }

  private async makeUpdateRequestParams(
    canonicalKey: string,
    existingDelegation: SecureDelegation,
    exchangeDataInfo: { exchangeData: ExchangeData; accessControlSecret: string; exchangeKey: CryptoKey },
    shareSecretIds: string[],
    shareEncryptionKeys: string[],
    shareOwningEntityIds: string[]
  ): Promise<EntitySharedMetadataUpdateRequest | undefined> {
    const existingSecretIds = new Set(await this.secureDelegationsEncryption.decryptSecretIds(existingDelegation, exchangeDataInfo.exchangeKey))
    const existingEncryptionKeys = new Set(
      await this.secureDelegationsEncryption.decryptEncryptionKeys(existingDelegation, exchangeDataInfo.exchangeKey)
    )
    const existingOwningEntityIds = new Set(
      await this.secureDelegationsEncryption.decryptOwningEntityIds(existingDelegation, exchangeDataInfo.exchangeKey)
    )
    const newSecretIds = shareSecretIds.filter((id) => !existingSecretIds.has(id))
    const newEncryptionKeys = shareEncryptionKeys.filter((key) => !existingEncryptionKeys.has(key))
    const newOwningEntityIds = shareOwningEntityIds.filter((id) => !existingOwningEntityIds.has(id))
    if (newSecretIds.length || newEncryptionKeys.length || newOwningEntityIds.length) {
      const encryptedNewSecretIds = await this.secureDelegationsEncryption.encryptSecretIds(newSecretIds, exchangeDataInfo.exchangeKey)
      const encryptedNewEncryptionKeys = await this.secureDelegationsEncryption.encryptEncryptionKeys(newEncryptionKeys, exchangeDataInfo.exchangeKey)
      const encryptedNewOwningEntityIds = await this.secureDelegationsEncryption.encryptOwningEntityIds(
        newOwningEntityIds,
        exchangeDataInfo.exchangeKey
      )
      return new EntitySharedMetadataUpdateRequest({
        metadataAccessControlHash: canonicalKey,
        secretIds: Object.fromEntries(encryptedNewSecretIds.map((id) => [id, EntryUpdateTypeEnum.CREATE])),
        encryptionKeys: Object.fromEntries(encryptedNewEncryptionKeys.map((key) => [key, EntryUpdateTypeEnum.CREATE])),
        owningEntityIds: Object.fromEntries(encryptedNewOwningEntityIds.map((id) => [id, EntryUpdateTypeEnum.CREATE])),
      })
    } else return undefined
  }

  private async makeShareRequestParams(
    exchangeDataInfo: { exchangeData: ExchangeData; accessControlSecret: string; exchangeKey: CryptoKey },
    accessControlKey: string,
    delegateId: string,
    shareSecretIds: string[],
    shareEncryptionKeys: string[],
    shareOwningEntityIds: string[],
    newDelegationPermissions: EntityShareRequest.RequestedPermissionInternal
  ): Promise<EntityShareRequest> {
    return new EntityShareRequest({
      ...(await this.makeSecureDelegationEncryptedData(exchangeDataInfo, delegateId, shareSecretIds, shareEncryptionKeys, shareOwningEntityIds)),
      requestedPermissions: newDelegationPermissions,
      accessControlKeys: [accessControlKey],
    })
  }

  private async makeSecureDelegationInfo(
    entity: EncryptedEntityWithType,
    delegateId: string,
    shareSecretIds: string[],
    shareEncryptionKeys: string[],
    shareOwningEntityIds: string[],
    permissions: AccessLevelEnum,
    parentDelegationKey: string | undefined
  ): Promise<{
    delegationKey: string
    accessControlKeyHex: string
    delegation: SecureDelegation
    encryptedExchangeDataId: { [fp: string]: string } | undefined
  }> {
    // Be wary of explicit delegator and explicit delegate
    const exchangeDataInfo = await this.exchangeDataManager.getOrCreateEncryptionDataTo(delegateId)
    const accessControlHash = await this.accessControlSecretUtils.secureDelegationKeyFor(exchangeDataInfo.accessControlSecret, entity.type)
    const accessControlKey = ua2hex(await this.accessControlSecretUtils.accessControlKeyFor(exchangeDataInfo.accessControlSecret, entity.type))
    const encryptedDelegationInfo = await this.makeSecureDelegationEncryptedData(
      exchangeDataInfo,
      delegateId,
      shareSecretIds,
      shareEncryptionKeys,
      shareOwningEntityIds
    )
    const delegation = new SecureDelegation({
      delegator: encryptedDelegationInfo?.explicitDelegator,
      delegate: encryptedDelegationInfo?.explicitDelegate,
      secretIds: encryptedDelegationInfo?.secretIds,
      encryptionKeys: encryptedDelegationInfo?.encryptionKeys,
      owningEntityIds: encryptedDelegationInfo?.owningEntityIds,
      parentDelegations: parentDelegationKey ? [parentDelegationKey] : undefined,
      exchangeDataId: encryptedDelegationInfo?.exchangeDataId,
      encryptedExchangeDataId: encryptedDelegationInfo?.encryptedExchangeDataId,
      permissions: permissions,
    })
    return {
      delegationKey: accessControlHash,
      accessControlKeyHex: accessControlKey,
      delegation,
      encryptedExchangeDataId: encryptedDelegationInfo?.encryptedExchangeDataId,
    }
  }

  private async makeSecureDelegationEncryptedData(
    exchangeDataInfo: { exchangeData: ExchangeData; accessControlSecret: string; exchangeKey: CryptoKey },
    delegateId: string,
    shareSecretIds: string[],
    shareEncryptionKeys: string[],
    shareOwningEntityIds: string[]
  ): Promise<{
    explicitDelegator?: string
    explicitDelegate?: string
    secretIds?: string[]
    encryptionKeys?: string[]
    owningEntityIds?: string[]
    exchangeDataId?: string
    encryptedExchangeDataId?: { [fp: string]: string } // TODO if secure delegation info, check cache before calculating
  }> {
    const selfId = await this.dataOwnerApi.getCurrentDataOwnerId()
    const exchangeDataIdInfo =
      delegateId === selfId
        ? this.makeExchangeDataIdInfoForSelf(selfId, exchangeDataInfo.exchangeData)
        : await this.makeExchangeDataIdInfoForDelegate(selfId, delegateId, exchangeDataInfo.exchangeData)
    const encryptedSecretIds = await this.secureDelegationsEncryption.encryptSecretIds(shareSecretIds, exchangeDataInfo.exchangeKey)
    const encryptedEncryptionKeys = await this.secureDelegationsEncryption.encryptEncryptionKeys(shareEncryptionKeys, exchangeDataInfo.exchangeKey)
    const encryptedOwningEntityIds = await this.secureDelegationsEncryption.encryptOwningEntityIds(shareOwningEntityIds, exchangeDataInfo.exchangeKey)
    return {
      ...exchangeDataIdInfo,
      secretIds: encryptedSecretIds,
      encryptionKeys: encryptedEncryptionKeys,
      owningEntityIds: encryptedOwningEntityIds,
    }
  }

  private async makeExchangeDataIdInfoForDelegate(
    selfId: string,
    delegateId: string,
    exchangeData: ExchangeData
  ): Promise<{
    exchangeDataId?: string
    encryptedExchangeDataId?: { [fp: string]: string }
    explicitDelegator?: string
    explicitDelegate?: string
  }> {
    const delegateInfo = await this.getDataOwnerInfo(delegateId)
    if (!delegateInfo.requiresAnonymousDelegations && !this.selfNeedsAnonymousDelegations) {
      return {
        explicitDelegator: selfId,
        explicitDelegate: delegateId,
        exchangeDataId: exchangeData.id,
      }
    } else if (delegateInfo.requiresAnonymousDelegations && !this.selfNeedsAnonymousDelegations) {
      return {
        explicitDelegator: selfId,
        encryptedExchangeDataId: await this.secureDelegationsEncryption.encryptExchangeDataId(
          exchangeData.id!,
          Object.fromEntries(this.userKeys.getSelfVerifiedKeys().map((keyInfo) => [fingerprintV1toV2(keyInfo.fingerprint), keyInfo.pair.publicKey]))
        ),
      }
    } else if (!delegateInfo.requiresAnonymousDelegations && this.selfNeedsAnonymousDelegations) {
      /**
       * Important: this requires that the exchange data signature also validates the authenticity of the public keys included in there.
       */
      const fingerprintsOfVerifiedExchangeData = new Set(Object.keys(exchangeData.exchangeKey))
      const delegateVerifiedKeys: { [fp: string]: CryptoKey } = {}
      for (const keyHex of [...delegateInfo.availablePublicKeysHexWithSha1, ...delegateInfo.availablePublicKeysHexWithSha256]) {
        const currFp = fingerprintV2(keyHex)
        const shaVersion = delegateInfo.availablePublicKeysHexWithSha1.includes(keyHex) ? ShaVersion.Sha1 : ShaVersion.Sha256
        if (fingerprintsOfVerifiedExchangeData.has(currFp)) {
          delegateVerifiedKeys[currFp] = await this.primitives.RSA.importKey('spki', ua2ab(hex2ua(keyHex)), ['encrypt'], shaVersion)
        }
      }
      if (!Object.keys(delegateVerifiedKeys).length)
        throw new Error('Illegal state: could not find any verified key for delegate in verified exchange data.')
      return {
        explicitDelegate: delegateId,
        encryptedExchangeDataId: await this.secureDelegationsEncryption.encryptExchangeDataId(exchangeData.id!, delegateVerifiedKeys),
      }
    } else return {}
  }

  private makeExchangeDataIdInfoForSelf(
    selfId: string,
    exchangeData: ExchangeData
  ): {
    exchangeDataId?: string
    encryptedExchangeDataId?: { [fp: string]: string }
    explicitDelegator?: string
    explicitDelegate?: string
  } {
    if (this.selfNeedsAnonymousDelegations) {
      return {}
    } else {
      return {
        exchangeDataId: exchangeData.id,
        explicitDelegator: selfId,
        explicitDelegate: selfId,
      }
    }
  }

  private async getDataOwnerInfo(dataOwnerId: string) {
    return this.dataOwnerInfoCache.get(dataOwnerId, async () => {
      const dataOwnerWithType = await this.dataOwnerApi.getCryptoActorStub(dataOwnerId)
      return {
        item: {
          requiresAnonymousDelegations: this.cryptoStrategies.dataOwnerRequiresAnonymousDelegation(dataOwnerWithType),
          availablePublicKeysHexWithSha1: Array.from(hexPublicKeysWithSha1Of(dataOwnerWithType.stub)),
          availablePublicKeysHexWithSha256: Array.from(hexPublicKeysWithSha256Of(dataOwnerWithType.stub)),
        },
      }
    })
  }
}
